import { AuthProviderEnum } from '@onesource/schemas';
import {
  getSession,
  LC_SOURCE_STORAGE_KEY,
  REFRESH_TOKEN_STORAGE_KEY,
  SESSION_STORAGE_KEY,
  setActiveAuthProvider,
  setTokenToAxios,
  USER_STORAGE_KEY,
} from '@onesource/services';
import { FC, ReactElement, useEffect, useState } from 'react';

export type TAuthProviderProps = {
  children: ReactElement;
};

// Run synchronously at module load — before axios interceptors or React can clear the session.
// If the stored JWT contains wp_token (legacy WordPress token), clear the session and reload
// so the app boots fresh with Supabase auth.
(() => {
  try {
    const token = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!token) {
      return;
    }

    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!('wp_token' in payload)) {
      return;
    }

    console.log('[AuthProvider] WordPress token detected at module load, forcing logout');

    localStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem(LC_SOURCE_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);

    console.log('[AuthProvider] Session cleared. Reloading...');
    window.location.reload();
  } catch {
    // Ignore errors — don't block app startup
  }
})();

export const AuthProvider: FC<TAuthProviderProps> = props => {
  const { children } = props;
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const initializeAuth = () => {
      // One-time migration: move legacy auth_provider key into featureFlags
      try {
        const legacyProvider = localStorage.getItem('auth_provider');
        if (legacyProvider) {
          if (legacyProvider === 'supabase') {
            setActiveAuthProvider(AuthProviderEnum.supabase);
          }
          localStorage.removeItem('auth_provider');
        }
      } catch {
        // Ignore migration errors
      }

      // Load token from localStorage and set it to axios
      const accessToken = getSession();
      if (accessToken) {
        setTokenToAxios(accessToken);
      }

      setIsReady(true);
    };

    initializeAuth();
  }, []);

  if (!isReady) {
    return null;
  }

  return children;
};
